import {
  DEFAULT_API_BASE_URL,
  type ContractAnalyzerAppApiClient,
  type ContractAnalyzerAppApiClientOptions,
  createContractAnalyzerAppApiClient,
} from '@contract-analyzer/api-client';

import { requestAuthRecovery } from '@/lib/auth-events';

function normalizeApiBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

function normalizeBasePath(basePath: string): string {
  return basePath.replace(/\/+$/, '');
}

function readAbsoluteUrl(baseUrl: string): URL | null {
  try {
    return new URL(baseUrl);
  } catch {
    return null;
  }
}

function prefixWithBasePath(path: string, basePath: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const normalizedBasePath = normalizeBasePath(basePath);
  return normalizeApiBaseUrl(
    normalizedBasePath ? `${normalizedBasePath}${normalizedPath}` : normalizedPath,
  );
}

export function resolveApiBaseUrl(
  configuredBaseUrl: string | undefined,
  basePath: string = import.meta.env.BASE_URL,
): string {
  const trimmedBaseUrl = configuredBaseUrl?.trim();
  if (!trimmedBaseUrl) {
    return prefixWithBasePath(DEFAULT_API_BASE_URL, basePath);
  }

  const absoluteUrl = readAbsoluteUrl(trimmedBaseUrl);
  if (absoluteUrl !== null) {
    if (absoluteUrl.protocol !== 'https:') {
      throw new Error('VITE_API_BASE_URL must use HTTPS when configured as an absolute URL.');
    }
    return normalizeApiBaseUrl(absoluteUrl.toString());
  }

  if (trimmedBaseUrl.startsWith('/')) {
    return normalizeApiBaseUrl(trimmedBaseUrl);
  }

  const normalizedConfiguredBaseUrl = `/${trimmedBaseUrl}`;
  const normalizedBasePath = normalizeBasePath(basePath);
  if (
    normalizedBasePath.length === 0 ||
    normalizedConfiguredBaseUrl === normalizedBasePath ||
    normalizedConfiguredBaseUrl.startsWith(`${normalizedBasePath}/`)
  ) {
    return normalizeApiBaseUrl(normalizedConfiguredBaseUrl);
  }

  return normalizeApiBaseUrl(`${normalizedBasePath}${normalizedConfiguredBaseUrl}`);
}

export const apiBaseUrl = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export const apiRedocUrl = `${apiBaseUrl}/public/redoc`;

export function createAppApiClient(userApiKey?: string): ContractAnalyzerAppApiClient {
  const apiKey = userApiKey?.trim() || '';
  const options: ContractAnalyzerAppApiClientOptions = {
    baseUrl: apiBaseUrl,
  };
  if (apiKey) {
    options.headers = { Authorization: `Bearer ${apiKey}` };
  } else {
    options.onAuthFailure = requestAuthRecovery;
  }
  return createContractAnalyzerAppApiClient(options);
}

export const apiClient = createContractAnalyzerAppApiClient({
  baseUrl: apiBaseUrl,
  onAuthFailure: requestAuthRecovery,
});
